import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';
import { toast } from 'react-toastify';

import Header from '../projects/veritas/invoice/components/Header';
import Footer from '../projects/veritas/invoice/components/Footer';
import SkipLinks, {
	MAIN_CONTENT_TARGET_ID,
} from '../projects/veritas/invoice/components/SkipLinks';
import PrivateRoute from './PrivateRoute';
import TariDocument from '../projects/veritas/invoice/tari/components/Document';
import TariInvoice from '../projects/veritas/invoice/tari/components/Invoice';
import TariDocumentList from '../projects/veritas/invoice/tari/components/DocumentList';
import IdricoDocument from '../projects/veritas/invoice/idrico/components/Document';
import IdricoInvoice from '../projects/veritas/invoice/idrico/components/Invoice';
import IdricoDocumentList from '../projects/veritas/invoice/idrico/components/DocumentList';
import { NotFound } from './NotFound';
import agent from '../api/agent';
import { Static } from '../static';
import { DocumentVeritas as DocumentVeritasTari } from '../projects/veritas/invoice/tari/models/documentVeritas';
import { DocumentVeritas as DocumentVeritasIdrico } from '../projects/veritas/invoice/idrico/models/documentVeritas';
import { Document as DocumentDemoIdrico } from '../projects/demo/invoice/idrico/models/document';

import { store } from '../context/store';
import Main from '../projects/iveco/recall-campaign/vehicle/components/Main';
import IvecoLogin from '../projects/iveco/recall-campaign/vehicle/components/Login';
import { Document } from '../projects/iveco/recall-campaign/vehicle/models/document';
import DemoLogin from '../projects/demo/invoice/luce/components/Login';
import DemoHeader from '../projects/demo/invoice/luce/components/Header';
import DemoIdricoHeader from '../projects/demo/invoice/idrico/components/Header';
import DemoIdricoFooter from '../projects/demo/invoice/idrico/components/Footer';
import DemoIdricoDocument from '../projects/demo/invoice/idrico/components/Document';

import ClientDetail from '../projects/demo/invoice/luce/components/ClientDetail';
import DemoMain from '../projects/demo/invoice/luce/components/Main';
import { Document as DemoDocument } from '../projects/demo/invoice/luce/models/document';
import ClientConsumption from '../projects/demo/invoice/luce/components/ClientConsumption';
import VeritasGone from '../projects/veritas/invoice/layout/Gone';
import VeritasNotFound from '../projects/veritas/invoice/layout/NotFound';
import UnhandledError from './UnhandledError';

import PsaMain from '../projects/psa/vehicle/components/Main';
import { PsaDocument } from '../projects/psa/vehicle/models/json';

import { Document as SmatIdricoDocumentModel } from '../projects/smat/invoice/idrico/models/jsonSmatIdrico';
import SmatIdricoHeader from '../projects/smat/invoice/idrico/components/Header';
import SmatIdricoFooter from '../projects/smat/invoice/idrico/components/Footer';
import SmatIdricoDocument from '../projects/smat/invoice/idrico/components/Document';
import SmatNewCustomer from '../projects/smat/invoice/idrico/components/NewCustomer';
import NewCustomer from '../projects/smat/invoice/idrico/components/NewCustomer';
import { BmwDocument } from '../projects/bmw/vehicle/models/json';
import BmwMain from '../projects/bmw/vehicle/components/BmwMain';
import { AppRouteHandle } from '../models/types';
import ClientLogin from './ClientLogin';
import { StellantisDocument } from '../projects/stellantis/vehicle/models/json';
import StellantisMain from '../projects/stellantis/vehicle/components/Main';

const router = createBrowserRouter([
	{
		path: '',
		element: <Outlet></Outlet>,
		errorElement: <Navigate to='/error' />,
		children: [
			{ index: true, element: <NotFound /> },
			{
				path: 'smat',
				handle: { brand: 'smat' } satisfies AppRouteHandle,
				lazy: async () => {
					let SmatMainContainer =
						await import('../projects/smat/invoice/components/SmatMainContainer');
					return { Component: SmatMainContainer.default };
				},
				children: [
					{
						index: true,
						element: <Navigate to='/not-found' />,
					},
					{
						path: 'newCustomer/:id',
						element: (
							<PrivateRoute>
								<NewCustomer />
							</PrivateRoute>
						),
					},
					{
						path: 'missingEmail/:id',
						loader: smatIdricoJsonLoader,
						element: (
							<PrivateRoute>
								<SmatNewCustomer />
							</PrivateRoute>
						),
					},
					{
						path: 'invoice',
						children: [
							{
								path: 'idrico/login/:id',
								element: <ClientLogin />,
							},
							{
								path: 'idrico',
								loader: smatIdricoJsonLoader,
								id: 'smatIdricoJsonLoader',
								element: (
									<>
										<SmatIdricoHeader />
										<Outlet />
										<SmatIdricoFooter />
									</>
								),
								children: [
									{
										index: true,
										element: <Navigate to='/not-found' />,
									},
									{
										path: 'document/:id',
										element: (
											<PrivateRoute>
												<SmatIdricoDocument />
											</PrivateRoute>
										),
									},
								],
							},
						],
					},
				],
			},
			{
				path: 'veritas',
				handle: {
					brand: 'Veritas',
					notFoundPath: '/veritas/not-found',
					gonePath: '/veritas/invoice/gone',
				} satisfies AppRouteHandle,
				lazy: async () => {
					let VeritasMainContainer =
						await import('../projects/veritas/invoice/components/VeritasMainContainer');
					return { Component: VeritasMainContainer.default };
				},
				children: [
					{
						index: true,
						element: <Navigate to='/veritas/not-found' />,
					},
					{
						path: 'not-found',
						element: <VeritasNotFound />,
					},
					{
						path: 'invoice',
						children: [
							{
								index: true,
								element: <Navigate to='/veritas/not-found' />,
							},
							{
								path: 'gone',
								element: <VeritasGone />,
							},
							{
								path: 'tari/login/:id',
								element: (
									<main>
										<ClientLogin />
									</main>
								),
							},
							{
								path: 'tari',
								loader: veritasTariJsonLoader,
								id: 'veritasTariJsonLoader',
								element: (
									<>
										<SkipLinks />
										<Header
											isTari
											loaderTari='veritasTariJsonLoader'
											loaderIdrico=''
										/>
										<main
											id={MAIN_CONTENT_TARGET_ID}
											className='app-skip-target'
											tabIndex={-1}
										>
											<Outlet />
										</main>
										<Footer />
									</>
								),
								children: [
									{
										index: true,
										element: <Navigate to='/veritas/not-found' />,
									},
									{
										path: 'document/:id',
										element: (
											<PrivateRoute>
												<TariDocument loader='veritasTariJsonLoader' />
											</PrivateRoute>
										),
									},
									{
										path: 'document-list/:id',
										element: (
											<PrivateRoute>
												<TariDocumentList loader='veritasTariJsonLoader' />
											</PrivateRoute>
										),
									},
									{
										path: 'document-list/:id/invoice/:key',
										element: (
											<PrivateRoute>
												<TariInvoice loader='veritasTariJsonLoader' />
											</PrivateRoute>
										),
									},
								],
							},
							{
								path: 'idrico/login/:id',
								element: (
									<main>
										<ClientLogin />
									</main>
								),
							},
							{
								path: 'idrico',
								loader: veritasIdricoJsonLoader,
								id: 'veritasIdricoJsonLoader',
								element: (
									<>
										<SkipLinks />
										<Header
											isTari={false}
											loaderTari=''
											loaderIdrico='veritasIdricoJsonLoader'
										/>
										<main
											id={MAIN_CONTENT_TARGET_ID}
											className='app-skip-target'
											tabIndex={-1}
										>
											<Outlet />
										</main>
										<Footer />
									</>
								),
								children: [
									{
										index: true,
										element: <Navigate to='/veritas/not-found' />,
									},
									{
										path: 'document/:id',
										element: (
											<PrivateRoute>
												<IdricoDocument loader='veritasIdricoJsonLoader' />
											</PrivateRoute>
										),
									},
									{
										path: 'document-list/:id',
										element: (
											<PrivateRoute>
												<IdricoDocumentList loader='veritasIdricoJsonLoader' />
											</PrivateRoute>
										),
									},
									{
										path: 'document-list/:id/invoice/:key',
										element: (
											<PrivateRoute>
												<IdricoInvoice loader='veritasIdricoJsonLoader' />
											</PrivateRoute>
										),
									},
								],
							},
						],
					},
				],
			},
			{
				path: 'iveco',
				lazy: async () => {
					let IvecoMainContainer =
						await import('../projects/iveco/recall-campaign/vehicle/components/IvecoMainContainer');
					return { Component: IvecoMainContainer.default };
				},
				children: [
					{
						index: true,
						element: <Navigate to='/not-found' />,
					},
					{
						path: 'recall-campaign',
						children: [
							{
								index: true,
								element: <Navigate to='/not-found' />,
							},
							{
								path: 'vehicle',
								children: [
									{
										index: true,
										element: <Navigate to='/not-found' />,
									},
									{
										path: 'document/:id',
										loader: ivecoJsonLoader,
										element: <Main />,
									},
									{
										path: 'login/:id',
										element: <IvecoLogin />,
									},
								],
							},
						],
					},
				],
			},
			{
				path: 'demo',
				lazy: async () => {
					let DemoMainContainer =
						await import('../projects/demo/invoice/components/DemoMainContainer');
					return { Component: DemoMainContainer.default };
				},
				children: [
					{
						index: true,
						element: <Navigate to='/not-found' />,
					},
					{
						path: 'invoice',
						children: [
							{
								index: true,
								element: <Navigate to='/not-found' />,
							},
							{
								path: 'luce',
								children: [
									{
										index: true,
										element: <Navigate to='/not-found' />,
									},
									{
										path: 'document/:id',
										id: 'demoJsonLoader',
										loader: demoJsonLoader,
										element: (
											<>
												<DemoHeader />
												<Outlet></Outlet>
											</>
										),
										children: [
											{
												path: 'client',
												element: <ClientDetail />,
											},
											{
												path: 'consumption',
												element: <ClientConsumption />,
											},
											{ path: '', element: <DemoMain /> },
										],
									},
									{
										path: 'login/:type/:id',
										element: <DemoLogin />,
									},
								],
							},
							{
								path: 'idrico',
								children: [
									{
										index: true,
										element: <Navigate to='/not-found' />,
									},
									{
										path: 'document/:id',
										id: 'demoIdricoJsonLoader',
										loader: demoIdricoJsonLoader,
										element: (
											<>
												<DemoIdricoHeader loader='demoIdricoJsonLoader' />
												<DemoIdricoDocument loader='demoIdricoJsonLoader' />
												<DemoIdricoFooter />
											</>
										),
									},
									{
										path: 'login/:id',
										element: <ClientLogin />,
									},
								],
							},
						],
					},
				],
			},
			{
				path: 'psa',
				handle: { brand: 'PSA' } satisfies AppRouteHandle,
				lazy: async () => {
					let psaMainContainer =
						await import('../projects/psa/vehicle/components/PsaVehicleMainContainer');
					return { Component: psaMainContainer.default };
				},
				children: [
					{
						index: true,
						element: <Navigate to='/not-found' />,
					},
					{
						path: 'vehicle',
						children: [
							{
								index: true,
								element: <Navigate to='/not-found' />,
							},
							{
								path: 'document/:id',
								loader: psaJsonLoader,
								element: <PsaMain />,
							},
							{
								path: 'login/:id',
								element: <ClientLogin />,
							},
						],
					},
				],
			},
			{
				path: 'stellantis',
				handle: { brand: 'Stellantis' } satisfies AppRouteHandle,
				lazy: async () => {
					let stellantisMainContainer =
						await import('../projects/stellantis/vehicle/components/StellantisVehicleMainContainer');
					return { Component: stellantisMainContainer.default };
				},
				children: [
					{
						index: true,
						element: <Navigate to='/not-found' />,
					},
					{
						path: 'vehicle',
						children: [
							{
								index: true,
								element: <Navigate to='/not-found' />,
							},
							{
								path: 'document/:id',
								loader: stellantisJsonLoader,
								element: <StellantisMain />,
							},
							{
								path: 'login/:id',
								element: <ClientLogin />,
							},
						],
					},
				],
			},
			{
				path: 'bmw',
				handle: { brand: 'BMW' } satisfies AppRouteHandle,
				lazy: async () => {
					let bmwMainContainer =
						await import('../projects/bmw/vehicle/components/BmwVehicleMainContainer');
					return { Component: bmwMainContainer.default };
				},
				children: [
					{
						index: true,
						element: <Navigate to='/not-found' />,
					},
					{
						path: 'vehicle',
						children: [
							{
								index: true,
								element: <Navigate to='/not-found' />,
							},
							{
								path: 'document/:id',
								loader: bmwJsonLoader,
								element: <BmwMain />,
							},
							{
								path: 'login/:id',
								element: <ClientLogin />,
							},
						],
					},
				],
			},
			{ path: 'not-found', element: <NotFound /> },
			{ path: '*', element: <NotFound /> },
		],
	},
	{
		path: 'error',
		element: <UnhandledError />,
	},
]);

type DocumentWithJson = {
	Json: unknown;
};

type DocumentJsonLoaderOptions<TDocument extends DocumentWithJson> = {
	useLatestDocumentData?: boolean;
	onDocumentLoaded?: (document: TDocument) => void;
};

async function documentJsonLoader<TDocument extends DocumentWithJson>(
	{ params }: any,
	options: DocumentJsonLoaderOptions<TDocument> = {},
) {
	store.loaderStore.setAppLoadingTrue();

	try {
		const documentId = Number(params.id);

		if (!params.id || !documentId) {
			return null;
		}

		const document = (await agent.Document.getDocument(
			documentId,
			options.useLatestDocumentData,
		)) as TDocument | null;

		if (!document) return null;

		options.onDocumentLoaded?.(document);

		return document.Json;
	} catch {
		toast.error(Static.errorInRetrievingData);
		return null;
	} finally {
		store.loaderStore.setAppLoadingFalse();
	}
}

async function veritasTariJsonLoader(loaderArgs: any) {
	return documentJsonLoader<DocumentVeritasTari>(loaderArgs);
}

async function veritasIdricoJsonLoader(loaderArgs: any) {
	return documentJsonLoader<DocumentVeritasIdrico>(loaderArgs);
}

async function ivecoJsonLoader(loaderArgs: any) {
	return documentJsonLoader<Document>(loaderArgs);
}

async function demoJsonLoader(loaderArgs: any) {
	return documentJsonLoader<DemoDocument>(loaderArgs, {
		onDocumentLoaded: (document) => {
			const jsonCustomer = [
				{
					key: Static.invoiceKey,
					value: document.Json.NumeroCliente.toString(),
				},
			];
			localStorage.setItem('customer', JSON.stringify(jsonCustomer));
		},
	});
}

async function demoIdricoJsonLoader(loaderArgs: any) {
	return documentJsonLoader<DocumentDemoIdrico>(loaderArgs);
}

async function psaJsonLoader(loaderArgs: any) {
	return documentJsonLoader<PsaDocument>(loaderArgs, {
		useLatestDocumentData: true,
	});
}

async function stellantisJsonLoader(loaderArgs: any) {
	return documentJsonLoader<StellantisDocument>(loaderArgs, {
		useLatestDocumentData: true,
	});
}

async function bmwJsonLoader(loaderArgs: any) {
	return documentJsonLoader<BmwDocument>(loaderArgs, {
		useLatestDocumentData: true,
	});
}

async function smatIdricoJsonLoader(loaderArgs: any) {
	return documentJsonLoader<SmatIdricoDocumentModel>(loaderArgs);
}

export default router;
